import React, { useEffect, useMemo, useState } from "react";
// mui
import { Grid2 } from "@mui/material";
//styling
import styles from "./SiteAddons.module.scss";
// local import
import SiteHeaderCard from "../../Components/HeaderComponent/Header";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import {
  addons_names,
  adminApi,
  lastSegmentPath,
  STAGING,
} from "../../../../../../Utils/roconConstants";
import {
  getStagingEnv,
  siteDetails,
  staging,
} from "../../../../../../Utils/sitePages";
import { formatFileSize } from "../../../../../../Utils/utils";
import CustomButton from "../../../../../../GlobalComponents/CustomButton/CustomButton";
import { ReactComponent as StorageIcon } from "../../../../AdminAssets/svg/Storage_plan.svg";
import { ReactComponent as PerformanceUpgradeIcon } from "../../../../AdminAssets/svg/PerformanceSite.svg";
import { ReactComponent as WAFIcon } from "../../../../AdminAssets/svg/waf.svg";
import { ReactComponent as PerformanceSitesIcon } from "../../../../AdminAssets/svg/performanceSites.svg";

const MAX_STORAGE_PACKS = 10;

/**
 * Site > Add-ons: what this (EBS) site already has (disk size, performance upgrade, WAF,
 * performance site) and a direct checkout for more storage or the performance upgrade,
 * so the user does not have to go to Billing > Add-ons and pick the site there again.
 * The checkout calls are the same ones Billing > Add-ons makes (AddonsBillingSummary).
 */
export default function SiteAddons() {
  const navigate = useNavigate();
  const params = useParams();
  const orgId = params.orgId;
  const siteId = params.siteId;
  const location = useLocation();
  const lastSegment = lastSegmentPath(location);

  const sites = useSelector((state) => state?.sitesInfo?.selectedSite);
  const organizationsInfo = useSelector(
    (state) => state.organizationsInfo.selectedOrg,
  );
  const userDetails = useSelector((state) => state.users?.userDetails);
  const selectedDomain = useSelector((state) => state.sitesInfo.selectedDomain);
  const allPlans = useSelector((state) => state.plansInfo?.allPlans);
  const specificPlans = useSelector(
    (state) => state.plansInfo?.specificPlan?.specificPlans,
  );

  // the add-on flags (storageGB, performanceUpgrade, ...) only come with the site overview list,
  // which the API layer stores at sitesInfo.siteDetails (its onSuccess fires twice, so the
  // store is the reliable source)
  const siteOverviewList = useSelector(
    (state) => state.sitesInfo?.siteDetails?.data,
  );
  const overview = useMemo(
    () =>
      (Array.isArray(siteOverviewList) ? siteOverviewList : []).find(
        (site) => String(site?.siteId) === String(siteId),
      ) || null,
    [siteOverviewList, siteId],
  );
  const [storagePacks, setStoragePacks] = useState(1);
  const [storageLoading, setStorageLoading] = useState(false);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [checkoutOpened, setCheckoutOpened] = useState(false);

  const siteData = siteDetails(sites);
  const livedom = siteData?.domain.find((item) => item.primary === "Y")?.name;
  const isStaging = staging(sites);
  const isEbsSite =
    sites?.ebsSite === true && String(sites?.siteId) === String(siteId);

  useEffect(() => {
    getStore().dispatch({
      type: Actions.API_GET_SELECTED_SITE_DETAILS,
      data: { siteId: siteId },
      meta: {
        onSuccess: (data) => {
          const selectedEnvData = data.stagingDomainsList.find(
            (list) =>
              list?.environment ===
              (selectedDomain?.environment === "STAGING" ? "STAGING" : "LIVE"),
          );
          getStore().dispatch({
            type: Actions.SET_SELECTED_DOMAIN,
            data: selectedEnvData,
          });
        },
        onError: () => {},
      },
    });
    getStore().dispatch({
      type: Actions.SET_SITE_DETAILS,
      data: {
        orgId: orgId,
        siteId: siteId,
        sitePage: lastSegment,
      },
    });
  }, [siteId, selectedDomain?.environment]);

  useEffect(() => {
    getStore().dispatch({
      type: Actions.API_SITE_OVERVIEW,
      data: { orgId: orgId ? orgId : -1 },
      // the API layer calls meta.onSuccess unconditionally, so both callbacks must exist
      meta: { onSuccess: () => {}, onError: () => {} },
    });
    if (!allPlans?.features?.licenses) {
      getStore().dispatch({ type: Actions.API_ALL_PLANS });
    }
    if (!specificPlans?.length) {
      getStore().dispatch({
        type: Actions.API_GET_SEPCIFIC_PLAN,
        data: { orgId: orgId },
      });
    }
  }, [orgId, siteId]);

  // ---- what the site has today ----
  const addOns = overview?.addOns || {};
  const extraStorageGb = Number(addOns?.storageGB) || 0;
  const hasPerformanceUpgrade = addOns?.performanceUpgrade === true;
  const hasWaf = addOns?.WAF === true || siteData?.wafStatus === true;
  const isPerformanceSite = addOns?.performanceSite === true;

  // STORAGE: used is bytes, limit is GB and already includes the packs bought before
  const storageUsage = sites?.featuresUsageRes?.find(
    (feature) => feature?.type === "STORAGE",
  );
  const storageUsedBytes = Number(storageUsage?.used) || 0;
  const storageLimitGb = parseInt(storageUsage?.limitOrAvailablity) || 0;
  const baseStorageGb = Math.max(storageLimitGb - extraStorageGb, 0);
  const storagePercent =
    storageLimitGb > 0
      ? Math.min(
          Math.round((storageUsedBytes / (storageLimitGb * 1024 ** 3)) * 100),
          100,
        )
      : 0;

  // ---- what can be bought (same catalog as Billing > Add-ons) ----
  const country = organizationsInfo?.country || userDetails?.country;
  const currency = country === "USA" ? "$" : "₹";
  const paymentType = country === "USA" ? "stripe" : "cashfree";
  const licenses = allPlans?.features?.licenses || [];
  const priceOf = (license) =>
    parseFloat(
      country === "USA"
        ? license?.pricing?.[0]?.salePrice
        : license?.pricing?.[0]?.salePriceInr,
    ) || 0;

  const storageLicense = licenses.find(
    (license) => license.licenseType === addons_names.STORAGE,
  );
  const storagePackGb =
    parseInt(
      storageLicense?.features?.find((feature) => feature.type === "STORAGE")
        ?.limitOrAvailablity,
    ) || 0;
  const storagePackPrice = priceOf(storageLicense);
  const performanceLicense = licenses.find(
    (license) => license.licenseType === addons_names.PERFORMANCE_UPGRADE,
  );
  const performancePrice = priceOf(performanceLicense);

  // Billing hides add-ons once the base plan has an expiry countdown; mirror that here
  const basePlan = specificPlans?.find((plan) => plan.basePlan === true);
  const planExpired =
    basePlan?.daysAfterExpiry !== undefined &&
    basePlan?.daysAfterExpiry !== null;

  const openCheckout = (data) => {
    const link = typeof data === "string" ? data.replace(/\\/g, "") : null;
    if (link) {
      window.open(link, "_blank");
      setCheckoutOpened(true);
    }
  };

  function buyStorage() {
    if (!storageLicense || storagePacks < 1 || storageLoading) {
      return;
    }
    setStorageLoading(true);
    getStore().dispatch({
      type:
        paymentType === "stripe"
          ? Actions.API_PAY_STORAGE_STRIPE
          : Actions.API_PAY_STORAGE_CASHFREE,
      data: {
        orgId: orgId ? orgId : -1,
        quantity: storagePacks,
        priceId: storageLicense?.pricing?.[0]?.stripePriceId,
        siteIds: String(siteId),
      },
      meta: {
        onSuccess: (data) => {
          openCheckout(data);
          setStorageLoading(false);
        },
        onError: () => setStorageLoading(false),
      },
    });
  }

  function buyPerformanceUpgrade() {
    if (!performanceLicense || hasPerformanceUpgrade || performanceLoading) {
      return;
    }
    setPerformanceLoading(true);
    getStore().dispatch({
      type:
        paymentType === "stripe"
          ? Actions.API_PAY_PERFORMANCE_UPGRADE_STRIPE
          : Actions.API_PAY_PERFORMANCE_UPGRADE_CASHFREE,
      data: {
        orgId: orgId ? orgId : -1,
        priceId: performanceLicense?.pricing?.[0]?.stripePriceId,
        siteId: siteId,
      },
      meta: {
        onSuccess: (data) => {
          openCheckout(data);
          setPerformanceLoading(false);
        },
        onError: () => setPerformanceLoading(false),
      },
    });
  }

  const activeName =
    selectedDomain?.environment === "STAGING"
      ? selectedDomain.domainName
      : siteData.siteName;

  return (
    <Grid2 className={styles.Container}>
      <Grid2 className={styles.header}>Add-ons</Grid2>
      {siteData?.siteName && (
        <SiteHeaderCard
          name={activeName}
          domain={
            livedom?.length > 0 && selectedDomain?.environment === "LIVE"
              ? livedom
              : activeName + "." + process.env.REACT_APP_TEMP_DOMAIN
          }
          development={isStaging}
          production={getStagingEnv(siteData.staging, STAGING.LIVE)}
          phpClick={() => adminApi("php")}
          wpClick={() =>
            adminApi(
              "wp",
              activeName,
              selectedDomain?.environment === "STAGING"
                ? selectedDomain.domainName
                : livedom?.length > 0
                  ? livedom
                  : null,
              selectedDomain?.environment === "STAGING" ? true : false,
            )
          }
          domainClick={() =>
            adminApi(
              "domain",
              activeName,
              selectedDomain?.environment === "STAGING"
                ? selectedDomain.domainName
                : livedom?.length > 0
                  ? livedom
                  : null,
              selectedDomain?.environment === "STAGING" ? true : false,
            )
          }
          siteId={siteId}
          orgId={orgId}
          image={siteData.imagePath ? siteData.imagePath : null}
          orgInfo={organizationsInfo}
          addOnWaf={siteData.wafStatus}
          addOnPerformanceSite={siteData.performanceSite}
        />
      )}

      {sites?.siteId && !isEbsSite ? (
        <Grid2 className={styles.notice}>
          Storage and performance upgrades are available for sites on the new
          infrastructure only. Other add-ons for this site are managed from{" "}
          <span
            className={styles.link}
            onClick={() => navigate(`/account/${orgId}/add-on`)}
          >
            Billing &gt; Add-ons
          </span>
          .
        </Grid2>
      ) : (
        <>
          {/* ---- current add-ons ---- */}
          <Grid2 className={styles.sectionTitle}>What this site has</Grid2>
          <Grid2 className={styles.cards}>
            <Grid2 className={styles.card}>
              <Grid2 className={styles.cardHead}>
                <StorageIcon className={styles.icon} />
                <Grid2 className={styles.cardTitle}>Storage</Grid2>
                {/* {extraStorageGb > 0 && (
                  <Grid2 className={styles.badge}>
                    +{extraStorageGb} GB bought
                  </Grid2>
                )} */}
              </Grid2>
              <Grid2 className={styles.big}>
                Total Storage:{" "}
                {storageLimitGb > 0 ? `${storageLimitGb} GB ` : "—"}
              </Grid2>
              <Grid2 className={styles.muted}>
                {storageLimitGb > 0
                  ? `${baseStorageGb} GB included with the site` +
                    (extraStorageGb > 0
                      ? ` + ${extraStorageGb} GB extra storage`
                      : "")
                  : "Disk size not available yet"}
              </Grid2>
              <Grid2 className={styles.bar}>
                <Grid2
                  className={styles.fill}
                  style={{ width: `${storagePercent}%` }}
                />
              </Grid2>
              <Grid2 className={styles.muted}>
                {formatFileSize(storageUsedBytes)} used
                {storageLimitGb > 0 ? ` (${storagePercent}%)` : ""}
              </Grid2>
            </Grid2>

            <Grid2 className={styles.card}>
              <Grid2 className={styles.cardHead}>
                <PerformanceUpgradeIcon className={styles.icon} />
                <Grid2 className={styles.cardTitle}>Performance Upgrade</Grid2>
                <Grid2
                  className={`${styles.badge} ${
                    hasPerformanceUpgrade ? styles.on : styles.off
                  }`}
                >
                  {hasPerformanceUpgrade ? "Active" : "Not purchased"}
                </Grid2>
              </Grid2>
              <Grid2 className={styles.muted}>
                {hasPerformanceUpgrade
                  ? "This site runs on an upgraded instance."
                  : "Moves this site to a bigger instance. One-time, per site."}
              </Grid2>
            </Grid2>

            <Grid2 className={styles.card}>
              <Grid2 className={styles.cardHead}>
                <WAFIcon className={styles.icon} />
                <Grid2 className={styles.cardTitle}>WAF</Grid2>
                <Grid2
                  className={`${styles.badge} ${hasWaf ? styles.on : styles.off}`}
                >
                  {hasWaf ? "Enabled" : "Not enabled"}
                </Grid2>
              </Grid2>
              <Grid2 className={styles.muted}>
                Web application firewall in front of the site.
              </Grid2>
            </Grid2>

            {isPerformanceSite && (
              <Grid2 className={styles.card}>
                <Grid2 className={styles.cardHead}>
                  <PerformanceSitesIcon className={styles.icon} />
                  <Grid2 className={styles.cardTitle}>Performance Site</Grid2>
                  <Grid2 className={`${styles.badge} ${styles.on}`}>
                    Active
                  </Grid2>
                </Grid2>
                <Grid2 className={styles.muted}>
                  This site is on the Performance Sites add-on.
                </Grid2>
              </Grid2>
            )}
          </Grid2>

          {/* ---- buy for this site ---- */}
          <Grid2 className={styles.sectionTitle}>Buy for this site</Grid2>
          {planExpired ? (
            <Grid2 className={styles.notice}>
              Add-ons cannot be bought while the plan is expired. Renew it from{" "}
              <span
                className={styles.link}
                onClick={() => navigate(`/account/${orgId}/plan-usage`)}
              >
                Billing
              </span>
              .
            </Grid2>
          ) : (
            <Grid2 className={styles.cards}>
              <Grid2 className={styles.card}>
                <Grid2 className={styles.cardHead}>
                  <StorageIcon className={styles.icon} />
                  <Grid2 className={styles.cardTitle}>Extra storage</Grid2>
                </Grid2>
                {storageLicense ? (
                  <>
                    <Grid2 className={styles.muted}>
                      {currency}
                      {storagePackPrice} per {storagePackGb} GB pack, one-time.
                    </Grid2>
                    <Grid2 className={styles.stepper}>
                      <button
                        type="button"
                        className={styles.stepBtn}
                        disabled={storagePacks <= 1}
                        onClick={() =>
                          setStoragePacks((n) => Math.max(1, n - 1))
                        }
                      >
                        −
                      </button>
                      <Grid2 className={styles.stepValue}>
                        {storagePacks} × {storagePackGb} GB ={" "}
                        {storagePacks * storagePackGb} GB
                      </Grid2>
                      <button
                        type="button"
                        className={styles.stepBtn}
                        disabled={storagePacks >= MAX_STORAGE_PACKS}
                        onClick={() =>
                          setStoragePacks((n) =>
                            Math.min(MAX_STORAGE_PACKS, n + 1),
                          )
                        }
                      >
                        +
                      </button>
                    </Grid2>
                    <Grid2 className={styles.total}>
                      Total: {currency}
                      {(storagePacks * storagePackPrice).toFixed(2)}
                      {country !== "USA" && (
                        <span className={styles.muted}> + GST</span>
                      )}
                    </Grid2>
                    <Grid2 className={styles.muted}>
                      After this purchase the disk will be{" "}
                      {storageLimitGb + storagePacks * storagePackGb} GB.
                    </Grid2>
                    <Grid2 className={styles.actions}>
                      <CustomButton
                        onClick={buyStorage}
                        loading={storageLoading}
                        disabled={storageLoading}
                      >
                        Buy storage
                      </CustomButton>
                    </Grid2>
                  </>
                ) : (
                  <Grid2 className={styles.muted}>
                    Storage packs are not available right now.
                  </Grid2>
                )}
              </Grid2>

              <Grid2 className={styles.card}>
                <Grid2 className={styles.cardHead}>
                  <PerformanceUpgradeIcon className={styles.icon} />
                  <Grid2 className={styles.cardTitle}>
                    Performance upgrade
                  </Grid2>
                </Grid2>
                {performanceLicense ? (
                  <>
                    <Grid2 className={styles.muted}>
                      {currency}
                      {performancePrice} one-time
                      {country !== "USA" ? " + GST" : ""}. Moves this site to a
                      bigger instance; the site restarts once after payment.
                    </Grid2>
                    <Grid2 className={styles.actions}>
                      {hasPerformanceUpgrade ? (
                        <Grid2 className={styles.total}>
                          Already upgraded. A site can be upgraded once.
                        </Grid2>
                      ) : (
                        <CustomButton
                          onClick={buyPerformanceUpgrade}
                          loading={performanceLoading}
                          disabled={performanceLoading}
                        >
                          Upgrade this site
                        </CustomButton>
                      )}
                    </Grid2>
                  </>
                ) : (
                  <Grid2 className={styles.muted}>
                    The performance upgrade is not available right now.
                  </Grid2>
                )}
              </Grid2>
            </Grid2>
          )}

          {/* {checkoutOpened && (
            <Grid2 className={styles.notice}>
              Checkout opened in a new tab. Once the payment goes through the
              add-on is applied to this site automatically; refresh this page to
              see it.
            </Grid2>
          )} */}
        </>
      )}
    </Grid2>
  );
}
